<template>
  <div class="login-page">
    <!-- Animated Background -->
    <div class="animated-bg">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="gradient-orb orb-3"></div>
      <div class="mesh-overlay"></div>
    </div>

    <!-- Login Container -->
    <div class="login-container">
      <!-- Branding Section -->
      <div class="branding-section">
        <div class="brand-content">
          <div class="logo-icon">
            <svg
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M24 4L44 14V34L24 44L4 34V14L24 4Z"
                stroke="currentColor"
                stroke-width="2"
                fill="none"
              />
              <path
                d="M24 16V32M16 24H32"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <h1 class="brand-title">Central de Saúde Digital</h1>
          <p class="brand-subtitle">Plataforma integrada de gestão em saúde</p>

          <div class="features-list">
            <div class="feature-item">
              <span class="feature-icon">🏥</span>
              <span>Gestão de Unidades de Saúde</span>
            </div>
            <div class="feature-item">
              <span class="feature-icon">👨‍⚕️</span>
              <span>Controle de Profissionais</span>
            </div>
            <div class="feature-item">
              <span class="feature-icon">📊</span>
              <span>Fila de Atendimento em Tempo Real</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Login Form Section -->
      <div class="form-section">
        <div class="login-card">
          <div class="card-header">
            <h2>Bem-vindo de volta</h2>
            <p>Entre com suas credenciais para acessar</p>
          </div>

          <form @submit.prevent="handleLogin" class="login-form">
            <div class="input-group">
              <label for="email">E-mail</label>
              <div class="input-wrapper">
                <span class="input-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                    />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </span>
                <input
                  id="email"
                  v-model="username"
                  type="email"
                  placeholder="seu@email.com"
                  required
                  autocomplete="email"
                />
              </div>
            </div>

            <div class="input-group">
              <label for="password">Senha</label>
              <div class="input-wrapper">
                <span class="input-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="••••••••"
                  required
                  autocomplete="current-password"
                />
                <button
                  type="button"
                  class="toggle-password"
                  @click="showPassword = !showPassword"
                  :title="showPassword ? 'Ocultar senha' : 'Mostrar senha'"
                >
                  <svg
                    v-if="!showPassword"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  <svg
                    v-else
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                    />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                </button>
              </div>
            </div>

            <button type="submit" class="submit-btn" :disabled="loading">
              <span v-if="loading" class="loading-spinner"></span>
              <span>{{ loading ? "Entrando..." : "Entrar" }}</span>
              <svg
                v-if="!loading"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>

            <p v-if="error" class="error-msg">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {{ error }}
            </p>

            <!-- Sugestão de recuperação após tentativas falhas -->
            <Transition name="fade">
              <div v-if="showRecoverySuggestion" class="recovery-suggestion">
                <p>Está com dificuldades para acessar?</p>
                <button
                  type="button"
                  class="recovery-link-btn"
                  @click="openForgotPassword"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M15 7a2 2 0 0 1 2 2m4 0a6 6 0 0 1-7.743 5.743L11 17H9v2H7v2H4a1 1 0 0 1-1-1v-2.586a1 1 0 0 1 .293-.707l5.964-5.964A6 6 0 1 1 21 9z"
                    />
                  </svg>
                  Recuperar acesso
                </button>
              </div>
            </Transition>
          </form>

          <!-- Link Esqueci minha senha -->
          <div class="forgot-password-section">
            <button
              type="button"
              class="forgot-password-btn"
              @click="openForgotPassword"
            >
              Esqueci minha senha
            </button>
          </div>

          <footer class="card-footer">
            <div class="register-prompt">
              <span>Não tem acesso?</span>
              <router-link to="/register" class="register-link"
                >Solicitar cadastro</router-link
              >
            </div>

            <div class="footer-divider"></div>

            <p class="support-text">Precisa de ajuda?</p>
            <div class="contact-info">
              <a
                href="mailto:atendimento@portaltelemedicina.com.br"
                class="contact-link"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                  />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                atendimento@portaltelemedicina.com.br
              </a>
              <a href="tel:+551125381455" class="contact-link">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                  />
                </svg>
                (11) 2538-1455
              </a>
            </div>
          </footer>
        </div>
      </div>
    </div>

    <!-- Modal de Recuperação de Senha -->
    <Teleport to="body">
      <Transition name="modal">
        <div
          v-if="showForgotModal"
          class="modal-overlay"
          @click.self="closeForgotModal"
        >
          <div class="forgot-modal">
            <!-- Estado: Formulário -->
            <template v-if="forgotState === 'form'">
              <div class="modal-header">
                <div class="modal-icon">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M15 7a2 2 0 0 1 2 2m4 0a6 6 0 0 1-7.743 5.743L11 17H9v2H7v2H4a1 1 0 0 1-1-1v-2.586a1 1 0 0 1 .293-.707l5.964-5.964A6 6 0 1 1 21 9z"
                    />
                  </svg>
                </div>
                <h3>Recuperar acesso</h3>
                <p>
                  Digite seu email para receber as instruções de recuperação
                </p>
              </div>

              <form @submit.prevent="handleForgotPassword" class="modal-form">
                <div class="input-group">
                  <label for="forgot-email">E-mail cadastrado</label>
                  <div class="input-wrapper">
                    <span class="input-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                        />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    </span>
                    <input
                      id="forgot-email"
                      v-model="forgotEmail"
                      type="email"
                      placeholder="seu@email.com"
                      required
                      autocomplete="email"
                      :disabled="forgotLoading"
                    />
                  </div>
                </div>

                <p v-if="forgotError" class="error-msg modal-error">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {{ forgotError }}
                </p>

                <div class="modal-actions">
                  <button
                    type="button"
                    class="btn-cancel"
                    @click="closeForgotModal"
                    :disabled="forgotLoading"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    class="btn-submit"
                    :disabled="forgotLoading || !forgotEmail"
                  >
                    <span v-if="forgotLoading" class="loading-spinner"></span>
                    {{ forgotLoading ? "Enviando..." : "Enviar instruções" }}
                  </button>
                </div>
              </form>
            </template>

            <!-- Estado: Sucesso -->
            <template v-else-if="forgotState === 'success'">
              <div class="modal-success">
                <div class="success-icon">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <h3>Verifique seu email</h3>
                <p>
                  Se existir uma conta com o email
                  <strong>{{ forgotEmail }}</strong
                  >, você receberá as instruções de recuperação em alguns
                  minutos.
                </p>
                <p class="security-note">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  Verifique também sua caixa de spam
                </p>
                <button
                  type="button"
                  class="btn-submit btn-full"
                  @click="closeForgotModal"
                >
                  Entendi
                </button>
              </div>
            </template>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { login, forgotPassword } from "@/services/authService";
import { getProfile } from "@/services/userService";
import { useAuthStore } from "@/stores/auth";
import { adminLevelFromBackend } from "@/constants/adminLevels";
import { logger } from "@/utils/logger";

export default {
  name: "LoginPage",
  data() {
    return {
      // Login
      username: "",
      password: "",
      error: null,
      loading: false,
      showPassword: false,
      failedAttempts: 0,

      // Forgot Password
      showForgotModal: false,
      forgotState: "form", // 'form' | 'success'
      forgotEmail: "",
      forgotLoading: false,
      forgotError: null,
    };
  },
  computed: {
    // Mostra sugestão de recuperação após 2+ tentativas falhas
    showRecoverySuggestion() {
      return this.failedAttempts >= 2 && this.error;
    },
  },
  methods: {
    async handleLogin() {
      this.error = null;
      this.loading = true;

      try {
        // Limpa estado anterior antes de novo login
        const auth = useAuthStore();
        auth.clearBeforeLogin();

        logger.debug("Login: Realizando login...");
        const result = await login(this.username, this.password);

        // Reset contador de tentativas em caso de sucesso
        this.failedAttempts = 0;

        logger.debug("Login: Response recebido", {
          profile_role_level: result.profile_role_level,
          profile_role_level_name: result.profile_role_level_name,
          password_change_required: result.password_change_required,
          hasProfile: !!result.profile,
          profileKeys: result.profile ? Object.keys(result.profile) : [],
        });

        // Usa o perfil do response do login se disponível e válido
        // Considera válido se tem pelo menos uma propriedade útil
        let profile = result.profile;
        const isValidProfile =
          profile &&
          (profile.email ||
            profile.first_name ||
            profile.id ||
            profile.profile_id);

        if (!isValidProfile) {
          logger.debug(
            "Login: Profile não veio ou está vazio, buscando via getProfile...",
          );
          try {
            profile = await getProfile();
            logger.debug("Login: Perfil carregado via getProfile", profile);
          } catch (profileError) {
            logger.warn(
              "Login: Erro ao buscar perfil completo, usando dados do login",
              profileError,
            );
            // Cria um perfil mínimo com dados do login para não bloquear
            profile = {
              email: this.username,
              first_name: "",
              last_name: "",
              profile_role_level: result.profile_role_level,
              profile_role_level_name: result.profile_role_level_name,
            };
            logger.debug("Login: Profile mínimo criado:", profile);
          }
        } else {
          logger.debug("Login: Perfil já veio no response do login", profile);
        }

        auth.setAuth({
          token: result.token,
          refreshToken: result.refresh_token,
          profile,
          // Converte profile_role_level de string para número (backend pode retornar "Default", "MasterAdmin", etc)
          profile_role_level: adminLevelFromBackend(result.profile_role_level),
          profile_role_level_name: result.profile_role_level_name || "",
          password_change_required: result.password_change_required || false,
        });

        // Verifica se precisa trocar senha
        if (result.password_change_required) {
          logger.debug("Login: Troca de senha obrigatória, redirecionando...");
          this.$router.replace("/change-password");
        } else {
          logger.debug("Login: Store atualizado, redirecionando para /home");
          this.$router.replace("/home");
        }
      } catch (err) {
        logger.error("Erro no login:", err);
        this.failedAttempts++;

        // Mensagem de erro mais específica e clara para o usuário
        if (err.message) {
          this.error = err.message;
        } else if (err.name === "TypeError") {
          this.error =
            "Erro de conexão. Verifique sua internet e tente novamente.";
        } else {
          this.error = "Não foi possível realizar o login. Tente novamente.";
        }

        // Log adicional para debug em mobile
        logger.debug("Detalhes do erro:", {
          name: err.name,
          message: err.message,
          stack: err.stack,
        });
      } finally {
        this.loading = false;
      }
    },

    // Forgot Password Methods
    openForgotPassword() {
      this.showForgotModal = true;
      this.forgotState = "form";
      this.forgotEmail = this.username || ""; // Pré-preenche com email do login
      this.forgotError = null;
    },

    closeForgotModal() {
      this.showForgotModal = false;
      this.forgotEmail = "";
      this.forgotError = null;
      this.forgotState = "form";
    },

    async handleForgotPassword() {
      if (!this.forgotEmail) return;

      this.forgotError = null;
      this.forgotLoading = true;

      try {
        await forgotPassword(this.forgotEmail);
        // Sempre mostra sucesso por segurança (não revela se email existe)
        this.forgotState = "success";
      } catch (err) {
        logger.error("Erro ao enviar recuperação:", err);
        this.forgotError = err.message || "Erro ao enviar. Tente novamente.";
      } finally {
        this.forgotLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ========================================
   MODERN LOGIN PAGE - Glass Morphism Design
   ======================================== */

.login-page {
  position: relative;
  width: 100vw;
  /* Fix para 100vh no iOS Safari */
  height: 100vh;
  height: 100dvh; /* Dynamic viewport height - melhor suporte mobile */
  min-height: -webkit-fill-available;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Inter", "Segoe UI", system-ui, sans-serif;
  /* Safe area para notch */
  padding: env(safe-area-inset-top) env(safe-area-inset-right)
    env(safe-area-inset-bottom) env(safe-area-inset-left);
}

/* Animated Background */
.animated-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #6366f1 100%);
  overflow: hidden;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.6;
  animation: float 20s ease-in-out infinite;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: linear-gradient(135deg, #22d3ee, #06b6d4);
  top: -200px;
  left: -100px;
  animation-delay: 0s;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #8b5cf6, #a78bfa);
  bottom: -150px;
  right: -100px;
  animation-delay: -7s;
}

.orb-3 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #f472b6, #ec4899);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: -14s;
}

@keyframes float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  25% {
    transform: translate(50px, -50px) scale(1.1);
  }
  50% {
    transform: translate(-30px, 30px) scale(0.95);
  }
  75% {
    transform: translate(40px, 40px) scale(1.05);
  }
}

.mesh-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(
      at 40% 20%,
      rgba(99, 102, 241, 0.15) 0px,
      transparent 50%
    ),
    radial-gradient(at 80% 0%, rgba(6, 182, 212, 0.15) 0px, transparent 50%),
    radial-gradient(at 0% 50%, rgba(139, 92, 246, 0.15) 0px, transparent 50%),
    radial-gradient(at 80% 50%, rgba(236, 72, 153, 0.1) 0px, transparent 50%),
    radial-gradient(at 0% 100%, rgba(34, 211, 238, 0.1) 0px, transparent 50%);
}

/* Login Container - Split Layout */
.login-container {
  position: relative;
  z-index: 10;
  display: flex;
  width: 100%;
  max-width: 1000px;
  min-height: 600px;
  margin: var(--space-4);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: var(--radius-2xl);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.18);
  overflow: hidden;
}

/* Branding Section */
.branding-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-10);
  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.9) 0%,
    rgba(79, 70, 229, 0.95) 100%
  );
  color: white;
}

.brand-content {
  max-width: 380px;
  text-align: center;
}

.logo-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto var(--space-6);
  padding: var(--space-4);
  background: rgba(255, 255, 255, 0.15);
  border-radius: var(--radius-xl);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.logo-icon svg {
  width: 48px;
  height: 48px;
}

.brand-title {
  font-size: var(--font-2xl);
  font-weight: 700;
  margin: 0 0 var(--space-2) 0;
  letter-spacing: -0.02em;
}

.brand-subtitle {
  font-size: var(--font-base);
  opacity: 0.9;
  margin: 0 0 var(--space-8) 0;
}

.features-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.feature-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background: rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg);
  font-size: var(--font-sm);
  text-align: left;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all var(--transition-base);
}

.feature-item:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateX(4px);
}

.feature-icon {
  font-size: 1.2rem;
}

/* Form Section */
.form-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-8);
  background: var(--bg-secondary);
}

.login-card {
  width: 100%;
  max-width: 400px;
}

.card-header {
  text-align: center;
  margin-bottom: var(--space-8);
}

.card-header h2 {
  font-size: var(--font-xl);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 var(--space-2) 0;
}

.card-header p {
  font-size: var(--font-base);
  color: var(--text-muted);
  margin: 0;
}

/* Form Styles */
.login-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.input-group label {
  font-size: var(--font-sm);
  font-weight: 500;
  color: var(--text-secondary);
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: var(--space-4);
  color: var(--text-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.input-wrapper input {
  width: 100%;
  padding: var(--space-4) var(--space-4) var(--space-4) var(--space-12);
  background: var(--bg-input);
  border: 1px solid var(--border-input);
  border-radius: var(--radius-lg);
  font-size: var(--font-base);
  color: var(--text-primary);
  transition: all var(--transition-base);
  box-sizing: border-box;
}

.input-wrapper input::placeholder {
  color: var(--text-subtle);
}

.input-wrapper input:focus {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
}

.toggle-password {
  position: absolute;
  right: var(--space-3);
  background: none;
  border: none;
  padding: var(--space-2);
  cursor: pointer;
  color: var(--text-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color var(--transition-fast);
  border-radius: var(--radius-sm);
}

.toggle-password:hover {
  color: var(--text-primary);
  background: var(--bg-hover);
}

/* Submit Button */
.submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-4) var(--space-6);
  background: var(--gradient-primary);
  color: white;
  font-weight: 600;
  font-size: var(--font-md);
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all var(--transition-base);
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.4);
  margin-top: var(--space-2);
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.5);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Error Message */
.error-msg {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--danger-bg);
  border: 1px solid var(--danger-border);
  border-radius: var(--radius-lg);
  color: var(--danger-text);
  font-size: var(--font-sm);
  font-weight: 500;
  margin: 0;
}

/* Footer */
.card-footer {
  margin-top: var(--space-8);
  padding-top: var(--space-6);
  border-top: 1px solid var(--border-secondary);
  text-align: center;
}

.register-prompt {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  font-size: var(--font-sm);
  color: var(--text-muted);
}

.register-link {
  color: var(--primary-500);
  font-weight: 600;
  text-decoration: none;
  transition: all var(--transition-fast);
}

.register-link:hover {
  color: var(--primary-400);
  text-decoration: underline;
}

.footer-divider {
  height: 1px;
  background: var(--border-secondary);
  margin: var(--space-4) 0;
}

.support-text {
  font-size: var(--font-sm);
  color: var(--text-muted);
  margin: 0 0 var(--space-3) 0;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.contact-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-sm);
  color: var(--text-muted);
  text-decoration: none;
  border-radius: var(--radius-md);
  transition: all var(--transition-fast);
}

.contact-link:hover {
  color: var(--primary-500);
  background: var(--bg-hover);
}

/* Responsive Design */
@media (max-width: 900px) {
  .login-container {
    flex-direction: column;
    max-width: 500px;
    min-height: auto;
  }

  .branding-section {
    padding: var(--space-8) var(--space-6);
  }

  .features-list {
    display: none;
  }

  .brand-subtitle {
    margin-bottom: 0;
  }

  .form-section {
    padding: var(--space-6);
  }
}

@media (max-width: 500px) {
  .login-container {
    margin: var(--space-3);
    border-radius: var(--radius-xl);
  }

  .branding-section {
    padding: var(--space-6) var(--space-4);
  }

  .logo-icon {
    width: 60px;
    height: 60px;
    margin-bottom: var(--space-4);
  }

  .logo-icon svg {
    width: 36px;
    height: 36px;
  }

  .brand-title {
    font-size: var(--font-xl);
  }

  .form-section {
    padding: var(--space-5);
  }

  .card-header {
    margin-bottom: var(--space-6);
  }

  .card-header h2 {
    font-size: var(--font-lg);
  }
}

/* Mobile pequeno (iPhone SE, etc) */
@media (max-width: 380px) {
  .login-container {
    margin: var(--space-2);
  }

  .branding-section {
    padding: var(--space-4) var(--space-3);
  }

  .logo-icon {
    width: 48px;
    height: 48px;
    margin-bottom: var(--space-3);
    padding: var(--space-2);
  }

  .logo-icon svg {
    width: 28px;
    height: 28px;
  }

  .brand-title {
    font-size: var(--font-lg);
  }

  .brand-subtitle {
    font-size: var(--font-sm);
  }

  .form-section {
    padding: var(--space-4);
  }

  .login-form {
    gap: var(--space-4);
  }

  .input-wrapper input {
    padding: var(--space-3) var(--space-3) var(--space-3) var(--space-10);
    font-size: var(--font-sm);
  }

  .submit-btn {
    padding: var(--space-3) var(--space-4);
    font-size: var(--font-sm);
  }

  .card-footer {
    margin-top: var(--space-6);
    padding-top: var(--space-4);
  }

  .contact-link {
    font-size: var(--font-xs);
  }
}

/* Mobile Landscape - otimiza para telas largas e baixas */
@media (max-height: 500px) and (orientation: landscape) {
  .login-page {
    overflow-y: auto;
  }

  .login-container {
    flex-direction: row;
    min-height: auto;
    max-height: none;
    margin: var(--space-3);
  }

  .branding-section {
    padding: var(--space-4);
    min-width: 280px;
  }

  .logo-icon {
    width: 48px;
    height: 48px;
    margin-bottom: var(--space-3);
  }

  .brand-title {
    font-size: var(--font-lg);
  }

  .brand-subtitle {
    font-size: var(--font-sm);
    margin-bottom: 0;
  }

  .features-list {
    display: none;
  }

  .form-section {
    padding: var(--space-4);
  }

  .card-header {
    margin-bottom: var(--space-4);
  }

  .card-header h2 {
    font-size: var(--font-base);
  }

  .login-form {
    gap: var(--space-3);
  }

  .card-footer {
    margin-top: var(--space-4);
    padding-top: var(--space-3);
  }

  .footer-divider {
    display: none;
  }

  .support-text,
  .contact-info {
    display: none;
  }
}

/* Touch device improvements */
@media (pointer: coarse) {
  .input-wrapper input {
    min-height: 48px; /* Mínimo recomendado para touch */
  }

  .submit-btn {
    min-height: 48px;
  }

  .toggle-password {
    min-width: 44px;
    min-height: 44px;
  }

  .forgot-password-btn {
    min-height: 44px;
    padding: var(--space-3);
  }

  .contact-link {
    min-height: 44px;
  }

  .register-link {
    padding: var(--space-2) var(--space-3);
  }
}

/* ========================================
   FORGOT PASSWORD SECTION & MODAL
   ======================================== */

/* Link "Esqueci minha senha" */
.forgot-password-section {
  text-align: center;
  margin-top: var(--space-4);
  padding-top: var(--space-2);
}

.forgot-password-btn {
  background: none;
  border: none;
  color: #6366f1;
  font-size: var(--font-sm);
  cursor: pointer;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  transition: all 0.2s ease;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.forgot-password-btn:hover {
  color: #4f46e5;
  background: rgba(99, 102, 241, 0.08);
  text-decoration: none;
}

/* Sugestão de recuperação após tentativas falhas */
.recovery-suggestion {
  margin-top: var(--space-4);
  padding: var(--space-4);
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: var(--radius-lg);
  text-align: center;
}

.recovery-suggestion p {
  color: #64748b;
  font-size: var(--font-sm);
  margin-bottom: var(--space-3);
}

.recovery-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: var(--radius-md);
  font-size: var(--font-sm);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.recovery-link-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--space-4);
}

/* Modal Container */
.forgot-modal {
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-xl);
  padding: var(--space-8);
  width: 100%;
  max-width: 420px;
  max-height: 90vh;
  max-height: 90dvh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

/* Modal responsivo para mobile */
@media (max-width: 480px) {
  .modal-overlay {
    padding: var(--space-3);
    align-items: flex-end; /* Modal sobe do fundo no mobile */
  }

  .forgot-modal {
    padding: var(--space-6);
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    max-width: none;
    margin-bottom: 0;
  }

  .modal-icon {
    width: 56px;
    height: 56px;
  }

  .modal-icon svg {
    width: 28px;
    height: 28px;
  }

  .modal-header h3 {
    font-size: var(--font-lg);
  }

  .modal-actions {
    flex-direction: column-reverse;
    gap: var(--space-2);
  }

  .btn-cancel,
  .btn-submit {
    width: 100%;
    flex: none;
    min-height: 48px;
  }

  .success-icon {
    width: 64px;
    height: 64px;
  }

  .success-icon svg {
    width: 32px;
    height: 32px;
  }
}

/* Modal em mobile landscape */
@media (max-height: 500px) and (orientation: landscape) {
  .modal-overlay {
    align-items: center;
  }

  .forgot-modal {
    padding: var(--space-4);
    max-height: 95vh;
    border-radius: var(--radius-lg);
  }

  .modal-icon {
    width: 48px;
    height: 48px;
    margin-bottom: var(--space-2);
  }

  .modal-header {
    margin-bottom: var(--space-3);
  }

  .modal-header h3 {
    font-size: var(--font-base);
    margin-bottom: var(--space-1);
  }

  .modal-actions {
    margin-top: var(--space-3);
  }

  .modal-success {
    padding: var(--space-2) 0;
  }

  .success-icon {
    width: 48px;
    height: 48px;
    margin-bottom: var(--space-3);
  }
}

/* Modal Header */
.modal-header {
  text-align: center;
  margin-bottom: var(--space-6);
}

.modal-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.2) 0%,
    rgba(139, 92, 246, 0.2) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--space-4);
  color: #a5b4fc;
}

.modal-header h3 {
  font-size: var(--font-xl);
  font-weight: 700;
  color: white;
  margin-bottom: var(--space-2);
}

.modal-header p {
  color: rgba(255, 255, 255, 0.6);
  font-size: var(--font-sm);
  line-height: 1.5;
}

/* Modal Form */
.modal-form .input-group {
  margin-bottom: var(--space-4);
}

.modal-form .input-group label {
  color: rgba(255, 255, 255, 0.7);
}

.modal-form .input-wrapper input {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  color: white;
}

.modal-form .input-wrapper input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.modal-form .input-wrapper input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.3);
}

.modal-form .input-icon {
  color: rgba(255, 255, 255, 0.5);
}

.modal-error {
  margin-bottom: var(--space-4);
}

/* Modal Actions */
.modal-actions {
  display: flex;
  gap: var(--space-3);
  margin-top: var(--space-6);
}

.btn-cancel {
  flex: 1;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-md);
  color: rgba(255, 255, 255, 0.8);
  font-size: var(--font-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

.btn-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-submit {
  flex: 2;
  padding: 12px 20px;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  border: none;
  border-radius: var(--radius-md);
  color: white;
  font-size: var(--font-sm);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.btn-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-full {
  width: 100%;
  flex: none;
}

/* Success State */
.modal-success {
  text-align: center;
  padding: var(--space-4) 0;
}

.success-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    rgba(16, 185, 129, 0.2) 0%,
    rgba(5, 150, 105, 0.2) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--space-5);
  color: #6ee7b7;
  animation: successPulse 0.5s ease-out;
}

@keyframes successPulse {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.modal-success h3 {
  font-size: var(--font-xl);
  font-weight: 700;
  color: white;
  margin-bottom: var(--space-4);
}

.modal-success p {
  color: rgba(255, 255, 255, 0.7);
  font-size: var(--font-sm);
  line-height: 1.6;
  margin-bottom: var(--space-4);
}

.modal-success strong {
  color: white;
  font-weight: 600;
}

.security-note {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-xs);
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: var(--space-6);
}

.security-note svg {
  color: #a5b4fc;
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .forgot-modal,
.modal-leave-to .forgot-modal {
  transform: scale(0.95) translateY(20px);
}

.modal-enter-to .forgot-modal,
.modal-leave-from .forgot-modal {
  transform: scale(1) translateY(0);
}

/* Fade transition for recovery suggestion */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
